/**
 * Self-contained audio control: a small speaker button, injected directly
 * into <body> (not through hud.ts), plus a document-level capture-phase
 * click listener that plays the UI tap/invalid-buzz SFX for clicks on
 * button-ish elements anywhere in the app (menu or in-game).
 *
 * Position is recomputed continuously from the *actual* rendered layout
 * (the top resource bar and, in-game, the advisor onboarding card) rather
 * than hardcoded breakpoints, so it tracks both the desktop and the taller
 * mobile (<=700px) top-bar layout, and never sits under the advisor banner
 * even though that banner goes full-width on narrow screens.
 */

import { armGestureResume, cycleVolumePreset, isMuted, toggleMuted } from './context';
import { playInvalidBuzz, playUiTap } from './sfx';

const STYLE_ID = 'ta-audio-style';
const BTN_ID = 'ta-audio-btn';

function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    #${BTN_ID} {
      position: fixed;
      top: 12px;
      right: 12px;
      z-index: 20;
      width: 34px;
      height: 34px;
      border-radius: 50%;
      background: var(--panel, #131824);
      border: 1px solid var(--border, #2a3345);
      color: var(--text, #d7e0ee);
      font-size: 15px;
      line-height: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      padding: 0;
      -webkit-tap-highlight-color: transparent;
      user-select: none;
      transition: border-color 0.15s, transform 0.1s;
    }
    #${BTN_ID}:hover {
      border-color: var(--accent, #4f8ef7);
    }
    #${BTN_ID}:active {
      transform: scale(0.94);
    }
    #${BTN_ID}[data-muted="true"] {
      color: var(--text-dim, #8b98ad);
    }
    #${BTN_ID}::after {
      content: attr(data-vol-label);
      position: absolute;
      bottom: -16px;
      right: 0;
      font-size: 9px;
      font-family: var(--mono, monospace);
      color: var(--text-dim, #8b98ad);
      background: var(--panel, #131824);
      border: 1px solid var(--border, #2a3345);
      border-radius: 4px;
      padding: 1px 4px;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.15s;
    }
    #${BTN_ID}.ta-flash::after {
      opacity: 1;
    }
  `;
  document.head.appendChild(style);
}

function iconFor(muted: boolean): string {
  return muted ? '\u{1F507}' : '\u{1F50A}'; // 🔇 / 🔊
}

let btn: HTMLButtonElement | null = null;
let flashTimer: number | null = null;

function flashVolumeLabel(pct: number): void {
  if (!btn) return;
  btn.dataset.volLabel = `${pct}%`;
  btn.classList.add('ta-flash');
  if (flashTimer !== null) window.clearTimeout(flashTimer);
  flashTimer = window.setTimeout(() => btn?.classList.remove('ta-flash'), 1200);
}

function refreshIcon(): void {
  if (!btn) return;
  const muted = isMuted();
  btn.textContent = iconFor(muted);
  btn.dataset.muted = String(muted);
  btn.setAttribute('aria-label', muted ? 'Unmute audio' : 'Mute audio');
  btn.title = muted ? 'Unmute (long-press / right-click to change volume)' : 'Mute (long-press / right-click to change volume)';
}

function repositionButton(): void {
  if (!btn) return;
  const bar = document.querySelector<HTMLElement>('.resource-bar');
  let top = 12;
  if (bar) {
    let bottom = bar.getBoundingClientRect().bottom;
    const advisor = document.querySelector<HTMLElement>('.advisor-card');
    if (advisor && !advisor.hidden) {
      bottom = Math.max(bottom, advisor.getBoundingClientRect().bottom);
    }
    top = Math.round(bottom) + 8;
  }
  btn.style.top = `${top}px`;
}

function createButton(): HTMLButtonElement {
  const b = document.createElement('button');
  b.id = BTN_ID;
  b.type = 'button';
  document.body.appendChild(b);

  let longPressTimer: number | null = null;
  let longPressFired = false;

  const doCycle = (): void => {
    const pct = Math.round(cycleVolumePreset() * 100);
    flashVolumeLabel(pct);
    refreshIcon();
  };

  b.addEventListener('contextmenu', (ev) => {
    ev.preventDefault();
    doCycle();
  });

  b.addEventListener('pointerdown', (ev) => {
    if (ev.button === 2) return; // handled by contextmenu
    longPressFired = false;
    longPressTimer = window.setTimeout(() => {
      longPressFired = true;
      doCycle();
    }, 550);
  });
  const clearLongPress = (): void => {
    if (longPressTimer !== null) {
      window.clearTimeout(longPressTimer);
      longPressTimer = null;
    }
  };
  b.addEventListener('pointerup', clearLongPress);
  b.addEventListener('pointerleave', clearLongPress);
  b.addEventListener('pointercancel', clearLongPress);

  b.addEventListener('click', (ev) => {
    if (longPressFired) {
      ev.preventDefault();
      ev.stopPropagation();
      return;
    }
    toggleMuted();
    refreshIcon();
  });

  return b;
}

/** Delegated tap SFX: capture-phase so it works regardless of which handler
 * (if any) consumes the click afterward. Filters to button-ish elements so
 * it never fires for e.g. clicking empty canvas. */
function wireTapSfx(): void {
  document.addEventListener(
    'click',
    (ev) => {
      const target = ev.target as HTMLElement | null;
      if (!target) return;
      if (target.id === BTN_ID) return; // the speaker button has its own sound-free toggle
      const interactive = target.closest('button, [class*="btn"], .faction-card, .menu-tab');
      if (interactive) {
        playUiTap();
        return;
      }
      // A click that landed on a grid/panel container rather than an
      // actionable item inside it reads as a "that didn't do anything"
      // miss — the closest we can detect from outside hud.ts/input.ts,
      // since native disabled buttons never dispatch click at all.
      const deadZone = target.closest('.btn-grid, .selection-panel, .action-row');
      if (deadZone && deadZone === target) {
        playInvalidBuzz();
      }
    },
    { capture: true },
  );
}

let initialized = false;

export function initAudioUi(): void {
  if (initialized) return;
  initialized = true;
  armGestureResume();
  ensureStyle();
  btn = createButton();
  refreshIcon();
  repositionButton();
  wireTapSfx();
  window.addEventListener('resize', repositionButton);
  // Layout can change (advisor card appearing, top bar reflowing) without a
  // resize event; a light interval is simpler and cheaper than wiring a
  // MutationObserver into two other agents' render loops.
  window.setInterval(repositionButton, 400);
}
